import d3 from 'd3';
import mori from 'mori';
import { appDefault } from '../config/config';

function round(number, precision) {
  const factor = Math.pow(10, precision);
  const tempNumber = number * factor;
  const roundedTempNumber = Math.round(tempNumber);
  return roundedTempNumber / factor;
}

export function kffIsNumber(n) {
  return !isNaN(parseFloat(n)) && isFinite(n);
}

export function kffNumericValue(n) {
  if (!n && n !== 0) {
    return false;
  } else if (kffIsNumber(n)) {
    return parseFloat(n);
  }

  const pattern = /\,|\%|\$|<|>/g;
  const d = parseFloat(n.toString().replace(pattern, ''));
  if (isFinite(d) && !n.toString().match(/[a-zA-Z]/g)) {
    return d;
  }
  return null;
}

/**
 * This takes a number/float and inserts the **US** formatting
 * of a number w/ commas, ie: a comma every 3 nums and a period for a decimal
 * @param  {Number} number number to "commatize"
 * @param  {String} sym    symbol to insert before the number
 * @return {String}        "commatized" number
 */
export function commatize(number, sym = '') {
  const negativeSign = '-';
  return number
    .toString()
    .split('')
    .reverse() // Flip it!
    .reduce((acc, str, idx, arr) => {
      const pointLoc = arr.indexOf('.');
      if (idx > pointLoc) { // Do period check
        const loc = (pointLoc > -1) ?
          idx - pointLoc :
          idx;

        if (loc % 3 === 0 && loc !== 0 && str !== negativeSign) {
          acc.push(',');
        }
      }
      acc.push(str);
      return acc;
    }, [])
    .concat([sym]) // push on symbole
    .reverse() // Flip it back!
    .join('') // Join it!
    .replace(`${sym}-`, `-${sym}`); // If number is negative, swap the symbol and the negative sign.
}

export function getSelectedItem(items) {
  return items.filter((x) => x.selected).shift();
}

export function getPrecision(column) {
  return appDefault.decimalPlacesMap[
    column ? column.Precision || 0 : 0
  ] || 0;
}

export function numberPrecision(value, precision, percent) {
  const multiplier = percent ? 100 : 1;
  const number = round((+value * multiplier), precision || 0);

  // Return formatted number, accounting for possible '-0'
  return +number.toString().replace(+number === 0 ? '-' : '', '');
}

export function getInferredType(name) {
  if (/%/.test(name) ||
      /percent/i.test(name)) {
    return 'percent';
  }

  if (/\$/.test(name) ||
      /USD/.test(name) ||
      /currency/i.test(name)) {
    return 'currency';
  }

  if (name && name.toLowerCase() === 'year') {
    return 'year';
  }

  if (/states in/i.test(name)) {
    return 'geography';
  }
  return 'default';
}

/**
 * Reduces an array of objects into an array of a field
 * @param  {array} tree     object/array to reduce
 * @param  {string} field   key of object to extract
 * @return {array}          array of fields
 */
export function pluck(tree, field) {
  if (typeof tree !== 'object') {
    return [];
  }

  return tree.reduce((arr, item) => {
    return (arr.indexOf(item[field]) < 0) ? arr.concat(item[field]) : arr;
  }, []);
}

/**
 * Maps two arrays to an object key-value, and runs
 * modulus on values array if it is shorter than the key array
 * @param  {array} keyArray     Array of values that should be the keys
 * @param  {array} valueArray   Array of values that should map to values
 * @return {object}             key-value object
 */
export function modZip(keyArray, valueArray) {
  return keyArray.reduce((obj, d, idx) => {
    const key = (idx < valueArray.length) ?
      idx :
      idx % valueArray.length;
    obj[d] = valueArray[key];
    return obj;
  }, {});
}

/**
 * Uses canvas.measureText to compute and return the width
 * of the given text of given font in pixels.
 *
 * @param {String} text The text to be rendered.
 * @param {String} font The css font descriptor that text is
 *                 to be rendered with (e.g. "bold 14px verdana").
 * @return {int}   the width of the text element
 */
export function getEstTextLength(text, font) {
  // @TODO re-use canvas object for better performance
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  context.font = font;
  const metrics = context.measureText(text);
  const width = metrics.width;
  // cleanup
  if (canvas.remove) {
    canvas.remove();
  }
  return width;
}

/**
 * Get the estimated amount of rows of text
 * @param  {string}  text  the test to calculate rows of
 * @param  {String}  font  the css font descriptor that the text is
 *                         to be rendered with (e.g. "bold 14px verdana").
 * @param  {int}  width  the width the text should wrap in
 * @return {int}  the number of rows that would be needed to see all text
 */
export function getEstTextRows(text, font, width) {
  return !text.split ? 1 : text
    .split(' ')
    .reduce((acc, curr) => {
      const splitHyphen = curr.split('-');

      if (splitHyphen.length === 1) {
        acc.push(curr);
        return acc;
      }

      splitHyphen.forEach((item, index, array) =>
        acc.push(index === (array.length - 1) ? item : `${item}-`)
      );

      return acc;
    }, [])
    .reduce(
      (acc, curr, idx) => textRowsReducer(font, width)(acc, curr, idx),
      [[]]).length;
}

/**
 * Reducer helper for getEstTextRows
 * @param  {String} font  the css font descriptor that the text is
 *                        to be rendered with (e.g. "bold 14px verdana").
 * @param  {int} width    the width the text should wrap in
 * @return {function}     the reducer function which builds an array of text rows
 */
function textRowsReducer(font, width) {
  return (acc, curr, idx) => {
    const line = [...acc[(acc.length - 1) % idx || 0], curr]
    .join(' ')
    .replace(/-\s/g, '-'); // Accounts for hyphenated text joining with ' '
    const lineLength = getEstTextLength(line, font);
    if (lineLength > width || width - lineLength < 1) {
      acc.push([curr]);
    } else {
      acc[((acc.length - 1) % idx) || 0].push(curr);
    }
    return acc;
  };
}

/**
 * Digs into an object, for a property, setup for digging into columns
 * @param {Object} the object to dig into,
 * @param {curried strings} 2, 2+nth elements are the keys to dig.
 *
 * @return {ANY} value of the deepest depth
 */
// @TODO, replace this so it uses the `...keys` syntax vs arguments
export function digColumnValue() {
  const args = Array.prototype.slice.call(arguments);
  const keys = args.slice(1, args.length);
  let ret = keys.reduce((prev, curr) => (prev) ? prev[curr] : prev, args[0]);

  if (ret === undefined) {
    ret = ['nested', ...keys].reduce(
      (prev, curr) => {
        if (prev[curr]) {
          return (prev[curr].children !== undefined) ?
            prev[curr].children :
            prev[curr];
        }
        return prev;
      },
      args[0]
    );
  }
  return ret;
}

/**
 * Trim csv
 */
export function getAndCleanCSVValues(csvStr, nullValue) {
  if (!csvStr || !csvStr.length) {
    return nullValue;
  }
  if (csvStr.indexOf(' && ') > -1) {
    return [].concat(csvStr.split(' && ')
      .map((parse) => getAndCleanCSVValues(parse, nullValue))
      .join(' && '));
  }

  return d3.csv.parseRows(csvStr).shift()
    .map((value) => value.trim().replace(/"([^"]+(?="))"/g, '$1'));
}

/**
 * Logging function for mori pass through
 */
export function log(text, immutable) {
  /* eslint-disable no-console */
  console.log(`${text}: `, mori.toJs(immutable));
  /* eslint-enable no-console */
  return immutable;
}

export function getScrollbarWidth(padding = 0) {
  const outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.width = '100px';
  outer.style.msOverflowStyle = 'scrollbar'; // needed for WinJS apps

  document.body.appendChild(outer);

  const widthNoScroll = outer.offsetWidth;
  // force scrollbars
  outer.style.overflow = 'scroll';

  // add innerdiv
  const inner = document.createElement('div');
  inner.style.width = '100%';
  outer.appendChild(inner);

  const widthWithScroll = inner.offsetWidth;

  // remove divs
  outer.parentNode.removeChild(outer);

  return (widthNoScroll - widthWithScroll) + padding;
}

/**
 * Safe deep delete (don't delete parents that have children)
 * @param  {array} targets   Keys to iterate through
 * @param  {object} context  Object to delete from
 * @param  {bool}   unsafe   force unsafe deletion mode
 * @return {bool}            Returns deleted status (or itself)
 */
export function safeDeepDelete(targets, context, unsafe = false) {
  if (typeof context === 'undefined') {
    return false;
  }

  if (unsafe && targets.length > 1) {
    return deepDelete(targets.slice(1), context[targets[0]]);
  } else if (!unsafe && targets.length > 1) {
    return safeDeepDelete(targets.slice(1), context[targets[0]]);
  }

  if (context[targets[0]] && (
    unsafe || Object.keys(context[targets[0]]).length === 0)
  ) {
    delete context[targets[0]];
    return Object.keys(context).length > 0;
  }
  return false;
}

/**
 * Simple is object check.
 * @param item
 * @returns {boolean}
*/
export function isObject(item) {
  return (
    item && typeof item === 'object' &&
      !Array.isArray(item) &&
        item !== null);
}

/**
 * Deep merge two objects.
 * https://gist.github.com/Salakar/1d7137de9cb8b704e48a
 * @param target
 * @param source
*/
export function deepMerge(target, source) {
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!target[key]) {
          Object.assign(target, { [key]: {} });
        }
        deepMerge(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    });
  }
  return target;
}

/**
 * Delete a deep nested object property
 * @param  {array} targets   Keys to iterate through
 * @param  {object} context  Object to delete from
 * @return {bool}            Returns deleted status (or itself)
 */
export function deepDelete(targets, context) {
  if (typeof context === 'undefined') {
    return false;
  }
  if (targets.length > 1) {
    return deepDelete(targets.slice(1), context[targets[0]]);
  }
  if (context[targets[0]]) {
    delete context[targets[0]];
    return Object.keys(context).length > 0;
  }
  return false;
}

/**
 * Find a deep nested object value
 * @param  {array} targets    keys to iterate through
 * @param  {object} context   object to find in
 * @return {bool}             if key has a solution
 */
export function deepFind(targets, context) {
  if (typeof context === 'undefined') {
    return false;
  }
  if (targets.length > 1) {
    return deepFind(targets.slice(1), context[targets[0]]);
  }
  return context[targets[0]] || false;
}

// Check if rows is url selected
export function isUrlSelected(keys, rows) {
  return !!keys.reduce(
    (prev, curr) => prev ? prev[curr] : prev,
    rows
  );
}

// Check if rows is all selected
export function isAllSelected(keys, rows) {
  if (typeof rows === 'undefined') {
    return false;
  }
  let temp = rows;
  return [...keys.slice(0, keys.length - 1)].some((curr) => {
    if (!temp) {
      return false;
    }
    if (temp[curr] &&
      (temp[curr].hasOwnProperty('all') ||
      Object.keys(temp[curr]).length === 0)
    ) {
      return true;
    }
    temp = temp[curr];
    return false;
  });
}

/**
 * Detects if browser is IE11
 * modified from the Angular methods for doing this
 */
export function isIE() {
  let msie = parseInt(
    (/msie (\d+)/.exec(navigator.userAgent.toLowerCase()) || [])[1],
    10
  );
  if (isNaN(msie)) {
    msie = parseInt(
      (
        /trident\/.*; rv:(\d+)/.exec(navigator.userAgent.toLowerCase()) || []
      )[1],
      10
    );
  }
  return !isNaN(msie);
}

/**
 * Use with mori.filter for an indexOf
 * @param  {collection} haystack
 * @param  {value} needle
 * @return {bool}
 */
export function indexOf(haystack, needle) {
  return mori.some((x) => (mori.isCollection(x) && mori.isCollection(needle)) ?
    (
      mori.equals(mori.first(x), mori.first(mori.first(needle))) &&
      mori.equals(mori.last(x), mori.last(mori.first(needle)))
    ) :
    mori.equals(x, needle),
  haystack);
}

export function maxWordWidth(string, fontStyle) {
  return string.split(' ')
    .reduce((acc, text) =>
      text.split('-').length > 1 ?
        text.split('-').reduce((acc1, text1) =>
          Math.max(getEstTextLength(text1, fontStyle), acc1), 0) :
        Math.max(getEstTextLength(text, fontStyle), acc), 0);
}

/**
 * Escapes HTML tags in a string of characters
 * https://gist.github.com/cvan/2817d07ef5f17fb66365
 *
 * @param {String} str A string of characters.
 * @param {Boolean} escapeQuotes (optional) Whether to escape quotation marks.
 * @return {String} The HTML-escaped string.
 */
export function escapeHTML(str, escapeQuotes) {
  if (!str || typeof str !== 'string') {
    return '';
  }

  const escaped = str.replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  if (escapeQuotes) {
    return escaped.replace(/"/g, '&quot;').replace(/'/g, '&apos;');
  }

  return escaped;
}



/** WEBPACK FOOTER **
 ** ../~/eslint-loader!./js/util/util.js
 **/